import React, { Fragment, ReactNode, useEffect } from "react";
import * as rxjs from "rxjs";
import { Observable, Observer } from "rxjs";

export const nl2br = (text: string): ReadonlyArray<ReactNode> =>
  text.split("\n").map((line: string, i: number) => (
    <Fragment key={i}>
      {line}
      <br />
    </Fragment>
  ));

export function useSubscribeOf<T>(
  createObservable: () => Observable<T>,
  observerOrNext?: Partial<Observer<T>> | ((value: T) => void),
): void {
  useEffect(() => {
    const observable = createObservable();
    const subscription = observable.subscribe(observerOrNext);
    return () => subscription.unsubscribe();
  }, []);
}

export function useSubscribe<T>(
  observable: Observable<T>,
  observerOrNext?: Partial<Observer<T>> | ((value: T) => void),
): void {
  useSubscribeOf(() => observable, observerOrNext);
}

interface PromisePendingState {
  readonly state: "pending";
}

interface PromiseFulfilledState<T> {
  readonly state: "fulfilled";
  readonly data: T;
}

interface PromiseRejectedState {
  readonly state: "rejected";
  readonly error: unknown;
}

type PromiseState<T> =
  | PromisePendingState
  | PromiseFulfilledState<T>
  | PromiseRejectedState;

export function usePromiseStateOf<T>(
  createPromise: () => Promise<T>,
): PromiseState<T> {
  const [promiseState, setPromiseState] = React.useState<PromiseState<T>>({
    state: "pending",
  });

  useThenOf(createPromise, {
    onFulfilled: (value) => {
      setPromiseState({
        state: "fulfilled",
        data: value,
      });
    },
    onRejected: (err) => {
      setPromiseState({
        state: "rejected",
        error: err,
      });
    },
  });

  return promiseState;
}

export function useThenOf<T>(
  createPromise: () => Promise<T>,
  args: {
    onFulfilled: (value: T) => void;
    onRejected: (value: unknown) => void;
  },
): void {
  useSubscribeOf(() => rxjs.from(createPromise()), {
    next: args.onFulfilled,
    error: args.onRejected,
  });
}
